import type {
  RotativoAlerta,
  RotativoBreakdown,
  RotativoCalculoMes,
  RotativoComparacao,
  RotativoResultsType,
} from "../calculadora-rotativo.d";
import {
  ALERTAS_ROTATIVO,
  CONSTANTES_ROTATIVO,
  ValidationMessages,
} from "../calculadora-rotativo.d";

export const parseNumericValue = (value: string): number => {
  return (
    Number.parseFloat(value.replace(/[^0-9,-]/g, "").replace(",", ".")) || 0
  );
};

const parseIntegerValue = (value: string): number => {
  const numero = Number.parseInt(value) || 0;
  return Math.max(0, numero);
};

const arredondarMoeda = (valor: number): number =>
  Math.round(valor * 100) / 100;

const validarTaxaAbusiva = (taxa: number): boolean => {
  return taxa > CONSTANTES_ROTATIVO.TAXA_ALERTA_ABUSIVA;
};

const calcularIOF = (
  valor: number,
  dias: number = CONSTANTES_ROTATIVO.DIAS_MES,
): { iofMensal: number; iofDiario: number } => {
  const iofMensal = valor * CONSTANTES_ROTATIVO.IOF_MENSAL;
  const iofDiario = valor * CONSTANTES_ROTATIVO.IOF_DIARIO * dias;

  return {
    iofMensal: iofMensal,
    iofDiario: iofDiario,
  };
};

const calcularJurosRotativo = (
  valorPrincipal: number,
  taxaJuros: number,
  diasPeriodo: number = CONSTANTES_ROTATIVO.DIAS_MES,
): number => {
  if (diasPeriodo < CONSTANTES_ROTATIVO.DIAS_MES) {
    const fatorTempo = diasPeriodo / CONSTANTES_ROTATIVO.DIAS_MES;
    return valorPrincipal * taxaJuros * fatorTempo;
  }

  const periodos = diasPeriodo / CONSTANTES_ROTATIVO.DIAS_MES;
  return valorPrincipal * (Math.pow(1 + taxaJuros, periodos) - 1);
};

const calcularEncargosAtraso = (
  saldoRotativo: number,
  diasAtraso: number,
  multaPercentual: number = CONSTANTES_ROTATIVO.MULTA_PADRAO,
  jurosPercentual: number = CONSTANTES_ROTATIVO.JUROS_MORA_PADRAO,
): { multa: number; jurosMora: number } => {
  if (diasAtraso <= 0) {
    return { multa: 0, jurosMora: 0 };
  }

  const multa = saldoRotativo * multaPercentual;

  const jurosMora =
    saldoRotativo *
    jurosPercentual *
    (diasAtraso / CONSTANTES_ROTATIVO.DIAS_MES);

  return {
    multa: multa,
    jurosMora: jurosMora,
  };
};

const verificarLimiteLegal = (
  valorOriginal: number,
  valorAtual: number,
): {
  atingiuLimite: boolean;
  percentualAtual: number;
  limiteMaximo: number;
} => {
  const limiteMaximo = valorOriginal * (1 + CONSTANTES_ROTATIVO.LIMITE_LEGAL);
  const percentualAtual = (valorAtual / valorOriginal) * 100;
  const atingiuLimite = valorAtual >= limiteMaximo;

  return {
    atingiuLimite,
    percentualAtual: percentualAtual,
    limiteMaximo: limiteMaximo,
  };
};

const calcular30DiasRotativo = (
  saldoRotativo: number,
  taxaJuros: number,
  valorFaturaOriginal: number,
  diasAtraso: number = 0,
  multaPercentual: number = CONSTANTES_ROTATIVO.MULTA_PADRAO,
  jurosPercentual: number = CONSTANTES_ROTATIVO.JUROS_MORA_PADRAO,
): RotativoCalculoMes => {
  const jurosRotativo = calcularJurosRotativo(saldoRotativo, taxaJuros, 30);

  const { iofMensal, iofDiario } = calcularIOF(saldoRotativo, 30);

  const { multa, jurosMora } = calcularEncargosAtraso(
    saldoRotativo,
    diasAtraso,
    multaPercentual,
    jurosPercentual,
  );

  const saldoFinal =
    saldoRotativo + jurosRotativo + iofMensal + iofDiario + multa + jurosMora;

  const { atingiuLimite } = verificarLimiteLegal(
    valorFaturaOriginal,
    saldoFinal,
  );

  const percentualAumentoSaldo =
    ((saldoFinal - saldoRotativo) / saldoRotativo) * 100;

  return {
    mes: 1,
    saldoInicial: saldoRotativo,
    jurosRotativo: jurosRotativo,
    iofMensal: iofMensal,
    iofDiario: iofDiario,
    multa: multa,
    jurosMora: jurosMora,
    totalMes: saldoFinal,
    saldoFinal: saldoFinal,
    percentualAumento: percentualAumentoSaldo,
    atingiuLimite: atingiuLimite,
  };
};

const gerarComparacaoRotativo = (
  valorFatura: number,
  valorPago: number,
  taxaJuros: number,
): RotativoComparacao => {
  const saldoRotativo = valorFatura - valorPago;
  const percentualMinimo = valorFatura * CONSTANTES_ROTATIVO.PERCENTUAL_MINIMO;

  const pagamentoMinimo = Math.max(percentualMinimo, valorPago);
  const saldoMinimo = valorFatura - pagamentoMinimo;
  const calculoMinimo = calcular30DiasRotativo(
    saldoMinimo,
    taxaJuros,
    valorFatura,
    0,
    CONSTANTES_ROTATIVO.MULTA_PADRAO,
    CONSTANTES_ROTATIVO.JUROS_MORA_PADRAO,
  );

  const taxaParcelamento = Math.min(
    taxaJuros * CONSTANTES_ROTATIVO.FATOR_TAXA_PARCELAMENTO,
    CONSTANTES_ROTATIVO.TAXA_PARCELAMENTO_MAX,
  );
  const taxaParcelamentoPercentual = (taxaParcelamento * 100).toFixed(1);

  const valorParcelado = saldoRotativo;
  const parcelaValue =
    valorParcelado *
    (taxaParcelamento /
      (1 -
        Math.pow(1 + taxaParcelamento, -CONSTANTES_ROTATIVO.PARCELAS_PADRAO)));
  const totalParcelamento = parcelaValue * CONSTANTES_ROTATIVO.PARCELAS_PADRAO;

  const totalQuitacao = saldoRotativo;

  return {
    cenarioMinimo: {
      descricao: `Pagar mínimo (R$ ${arredondarMoeda(pagamentoMinimo).toFixed(2).replace(".", ",")})`,
      totalPagar: calculoMinimo.totalMes,
      totalJuros:
        calculoMinimo.jurosRotativo +
        calculoMinimo.iofMensal +
        calculoMinimo.iofDiario,
      tempo: 1,
    },
    cenarioParcelamento: {
      descricao: `Parcelar em 12x (taxa est. ${taxaParcelamentoPercentual}% a.m.)`,
      totalPagar: totalParcelamento,
      totalJuros: totalParcelamento - valorParcelado,
      tempo: CONSTANTES_ROTATIVO.PARCELAS_PADRAO,
    },
    cenarioQuitacao: {
      descricao: "Quitar saldo restante à vista",
      totalPagar: totalQuitacao,
      totalJuros: 0,
      tempo: 0,
    },
  };
};

const gerarAlertasRotativo = (
  breakdown: RotativoBreakdown,
  calculoMes: RotativoCalculoMes,
): RotativoAlerta[] => {
  const alertas: RotativoAlerta[] = [];

  alertas.push({
    tipo: "info" as const,
    titulo: "Importante - Regulamentação do Rotativo",
    icone: "⚖️",
    mensagem:
      "Limite de 30 dias: Após este período, a dívida será parcelada. Este cálculo mostra o valor da dívida após 30 dias (SEM incluir nova fatura). Próximo mês: O pagamento abaixo do mínimo pode não ser aceito.",
  });

  // Alerta sobre taxa abusiva
  if (validarTaxaAbusiva(breakdown.taxaJurosMensal)) {
    alertas.push({
      ...ALERTAS_ROTATIVO.TAXA_ALTA,
      mensagem: `Taxa de ${(breakdown.taxaJurosMensal * 100).toFixed(1)}% ao mês está muito elevada. A média do mercado é 13,9% ao mês. Considere negociar com o banco ou buscar outras modalidades de crédito.`,
    });
  }

  alertas.push({
    ...ALERTAS_ROTATIVO.EDUCATIVO_ROTATIVO,
    mensagem:
      "O crédito rotativo tem as maiores taxas do mercado e pode comprometer seriamente sua estabilidade financeira. Sempre que possível, quite a fatura integral ou negocie um parcelamento.",
  });

  return alertas;
};

const validarParametrosRotativo = (
  valorFatura: number,
  valorPago: number,
  taxaJuros: number,
  diasAtraso: number,
): void => {
  if (valorFatura <= 0) {
    throw new Error(ValidationMessages.VALOR_FATURA_INVALIDO);
  }

  if (valorPago > valorFatura) {
    throw new Error(ValidationMessages.VALOR_PAGO_MAIOR_FATURA);
  }

  if (taxaJuros <= 0) {
    throw new Error(ValidationMessages.TAXA_JUROS_INVALIDA);
  }

  if (validarTaxaAbusiva(taxaJuros)) {
    console.warn(ValidationMessages.TAXA_JUROS_ABUSIVA);
  }

  if (diasAtraso < 0) {
    throw new Error(ValidationMessages.DIAS_ATRASO_INVALIDO);
  }
};

export const calcularRotativo = (
  valorFatura: string,
  valorPago: string,
  taxaJuros: string,
  diasAtraso: string = "0",
  multaAtraso: string = "2",
  jurosMora: string = "1",
): RotativoResultsType => {
  const valorFaturaNum = parseNumericValue(valorFatura);
  const valorPagoNum = parseNumericValue(valorPago);
  const taxaJurosNum = parseNumericValue(taxaJuros) / 100; // Converte para decimal
  const diasAtrasoNum = parseIntegerValue(diasAtraso);
  const multaAtrasoNum = parseNumericValue(multaAtraso) / 100;
  const jurosMoraNum = parseNumericValue(jurosMora) / 100;

  validarParametrosRotativo(
    valorFaturaNum,
    valorPagoNum,
    taxaJurosNum,
    diasAtrasoNum,
  );

  const saldoRotativo = valorFaturaNum - valorPagoNum;

  const calculoMes = calcular30DiasRotativo(
    saldoRotativo,
    taxaJurosNum,
    valorFaturaNum,
    diasAtrasoNum,
    multaAtrasoNum,
    jurosMoraNum,
  );

  const { percentualAtual, limiteMaximo } = verificarLimiteLegal(
    valorFaturaNum,
    calculoMes.saldoFinal,
  );

  const breakdown: RotativoBreakdown = {
    valorOriginal: arredondarMoeda(valorFaturaNum),
    valorPago: arredondarMoeda(valorPagoNum),
    saldoRotativo: arredondarMoeda(saldoRotativo),
    taxaJurosMensal: taxaJurosNum,
    diasAtraso: diasAtrasoNum,
    calculoDetalhado: {
      jurosRotativo: arredondarMoeda(calculoMes.jurosRotativo),
      iofMensal: arredondarMoeda(calculoMes.iofMensal),
      iofDiario: arredondarMoeda(calculoMes.iofDiario),
      multa: arredondarMoeda(calculoMes.multa),
      jurosMora: arredondarMoeda(calculoMes.jurosMora),
    },
    limiteReglamentario: arredondarMoeda(limiteMaximo),
    percentualLimite: arredondarMoeda(percentualAtual),
  };

  const comparacao = gerarComparacaoRotativo(
    valorFaturaNum,
    valorPagoNum,
    taxaJurosNum,
  );

  const alertas = gerarAlertasRotativo(breakdown, calculoMes);

  const totalJurosAcumulados =
    calculoMes.jurosRotativo +
    calculoMes.iofMensal +
    calculoMes.iofDiario +
    calculoMes.multa +
    calculoMes.jurosMora;
  const custoAdicionalRotativo = calculoMes.saldoFinal - saldoRotativo;

  return {
    breakdown,
    calculoMeses: [calculoMes],
    comparacao,
    alertas,
    totalJurosAcumulados: arredondarMoeda(totalJurosAcumulados),
    tempoQuitacao: 1,
    valorFinalProjetado: arredondarMoeda(calculoMes.saldoFinal),
    economiaQuitacao: arredondarMoeda(custoAdicionalRotativo),
  };
};
